// File: src/frontend/src/components/Layout/Navbar.tsx - Added logo icon to match Footer
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo from '../Logo/Logo';

const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Ref for dropdown to handle click outside
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Track scroll position for navbar background
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown when route changes
  useEffect(() => {
    setUserDropdownOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Helper to check if a path is active
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // ============================================================================
  // UPGRADE BUTTON LOGIC
  // ============================================================================
  const shouldShowUpgradeButton = () => {
    if (!isAuthenticated || !user?.plan) return false;
    
    // Don't show for team-pilot or if already on highest plan
    if (user.plan.planType === 'team-pilot') return false;
    if (user.plan.planType === 'solo-pro' && !user.plan.isTrialActive) return false;
    
    return true;
  };

  const getUpgradeButtonProps = () => {
    const plan = user?.plan;
    const isTrialActive = plan?.isTrialActive || false;
    const trialDaysLeft = plan?.trialDaysLeft || 0;
    const creditsUsed = plan?.creditsUsed || 0;
    const creditsTotal = plan?.creditsTotal || 20;
    const creditsExhausted = creditsUsed >= creditsTotal;

    // Determine urgency level
    let urgency: 'high' | 'medium' | 'low' = 'low';
    let buttonText = 'Upgrade';
    let buttonStyle = 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700';

    if (isTrialActive && creditsExhausted) {
      urgency = 'high';
      buttonText = 'Upgrade Now';
      buttonStyle = 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 animate-pulse';
    } else if (isTrialActive && trialDaysLeft <= 3) {
      urgency = 'high';
      buttonText = 'Trial Ending';
      buttonStyle = 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600';
    } else if (isTrialActive && (creditsUsed / creditsTotal) >= 0.8) {
      urgency = 'medium';
      buttonText = 'Upgrade Trial';
      buttonStyle = 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600';
    } else if (plan?.planType === 'solo-core') {
      buttonText = 'Upgrade to Pro';
    }

    return { urgency, buttonText, buttonStyle };
  };

  const { buttonText, buttonStyle } = shouldShowUpgradeButton() ? getUpgradeButtonProps() : { buttonText: '', buttonStyle: '' };

  return (
    <>
      <nav className="fixed w-full top-0 z-[100] bg-gray-900 shadow-lg transition-all duration-300">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              {/* ENHANCED: Logo with icon matching Footer design */}
              <Link to="/" className="flex items-center text-xl font-bold text-white">
                <Logo width={40} height={40} showText={true} textClassName="text-white" />
              </Link>
              
              <div className="hidden md:block">
                <div className="ml-10 flex items-baseline space-x-4">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/dashboard"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/dashboard') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/learning-tracks"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/learning-tracks') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Learning Tracks
                      </Link>
                      <Link
                        to="/coaching-sessions"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/coaching-sessions') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Browse Scenarios
                      </Link>
                      <Link
                        to="/custom-scenarios"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/custom-scenarios') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Custom Scenarios
                      </Link>
                      <Link
                        to="/coaching-history"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/coaching-history') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Progress
                      </Link>
                      {user?.plan?.isTeamAdmin && (
                        <Link
                          to="/team-admin"
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                            isActive('/team-admin') 
                              ? 'bg-white/20 text-white backdrop-blur-sm' 
                              : 'text-white/90 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          Team Admin
                        </Link>
                      )}
                    </>
                  ) : (
                    <>
                      <Link
                        to="/"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Home
                      </Link>
                      <Link
                        to="/pricing"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/pricing') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Pricing
                      </Link>
                      <Link
                        to="/contact"
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                          isActive('/contact') 
                            ? 'bg-white/20 text-white backdrop-blur-sm' 
                            : 'text-white/90 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        Contact
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-4 flex items-center md:ml-6 space-x-3">
                {isAuthenticated ? (
                  <>
                    {/* ============================================================================ */}
                    {/* PERSISTENT UPGRADE BUTTON - ALWAYS VISIBLE FOR ELIGIBLE USERS */}
                    {/* ============================================================================ */}
                    {shouldShowUpgradeButton() && (
                      <Link
                        to="/pricing?source=navbar"
                        className={`
                          inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-md 
                          transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105
                          ${buttonStyle}
                        `}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        {buttonText}
                      </Link>
                    )}
                    
                    {/* User Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                        className="flex items-center text-white/90 hover:text-white transition-colors"
                      >
                        <span className="mr-2">Welcome, {user?.name}</span>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {userDropdownOpen && (
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                          {/* User Info Header */}
                          <div className="px-4 py-3 border-b border-gray-200">
                            <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                            <p className="text-xs text-gray-600">{user?.email}</p>
                            {user?.plan && user.plan.planType && (
                              <p className="text-xs text-blue-600 font-medium mt-1">
                                {user.plan.planType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                {user.plan.isTrialActive && ` Trial (${user.plan.trialDaysLeft || 0} days left)`}
                              </p>
                            )}
                          </div>
                          
                          {/* ============================================================================ */}
                          {/* UPGRADE OPTION IN USER DROPDOWN - ALWAYS ACCESSIBLE */}
                          {/* ============================================================================ */}
                          {shouldShowUpgradeButton() && (
                            <>
                              <Link
                                to="/pricing?source=dropdown"
                                className="group flex items-center px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors font-medium"
                                onClick={() => setUserDropdownOpen(false)}
                              >
                                <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                </svg>
                                {user?.plan?.planType === 'solo-core' ? 'Upgrade to Pro' : 'View Plans'}
                                {user?.plan?.isTrialActive && (
                                  <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">
                                    Trial
                                  </span>
                                )}
                              </Link>
                              <div className="border-t border-gray-200"></div>
                            </>
                          )}
                          
                          <Link
                            to="/settings"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Account Settings
                          </Link>
                          
                          <Link
                            to="/coaching-history"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            onClick={() => setUserDropdownOpen(false)}
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
                            </svg>
                            Progress & History
                          </Link>
                          
                          <a
                            href="mailto:support@pmcoach.app"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Get Support
                          </a>
                          
                          <div className="border-t border-gray-200"></div>
                          <button
                            onClick={() => {
                              handleLogout();
                              setUserDropdownOpen(false);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Logout
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center space-x-3">
                    <Link
                      to="/login"
                      className="px-3 py-2 rounded-md text-sm font-medium text-white/90 hover:bg-white/10 hover:text-white transition-all duration-200"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 rounded-md text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      Start Free Trial
                    </Link>
                  </div>
                )}
              </div>
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-all duration-200"
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ============================================================================ */}
        {/* MOBILE MENU WITH UPGRADE ACCESS */}
        {/* ============================================================================ */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-900/95 backdrop-blur-lg">
            <div className="space-y-1 px-2 pt-2 pb-3">
              {isAuthenticated ? (
                <>
                  {/* Mobile Upgrade Button - Top Priority */}
                  {shouldShowUpgradeButton() && (
                    <Link
                      to="/pricing?source=mobile"
                      className={`
                        block w-full text-center px-4 py-3 rounded-md text-base font-medium text-white mb-3
                        ${buttonStyle}
                      `}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      🚀 {buttonText}
                    </Link>
                  )}
                  
                  <Link
                    to="/dashboard"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/dashboard') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/learning-tracks"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/learning-tracks') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Learning Tracks
                  </Link>
                  <Link
                    to="/coaching-sessions"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/coaching-sessions') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Browse Scenarios
                  </Link>
                  <Link
                    to="/custom-scenarios"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/custom-scenarios') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Custom Scenarios
                  </Link>
                  <Link
                    to="/coaching-history"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/coaching-history') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Progress
                  </Link>
                  {user?.plan?.isTeamAdmin && (
                    <Link
                      to="/team-admin"
                      className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                        isActive('/team-admin') 
                          ? 'bg-white/20 text-white' 
                          : 'text-white/90 hover:bg-white/10 hover:text-white'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Team Admin
                    </Link>
                  )}
                  <hr className="border-gray-700 my-2" />
                  <div className="px-3 py-2 text-white">
                    <span>Welcome, {user?.name}</span>
                    {user?.plan && user.plan.planType && (
                      <div className="text-xs text-white/80 mt-1">
                        {user.plan.planType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        {user.plan.isTrialActive && ` Trial`}
                      </div>
                    )}
                  </div>
                  <Link
                    to="/settings"
                    className="block px-3 py-2 rounded-md text-base font-medium text-white/90 hover:bg-white/10 hover:text-white transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-white bg-red-600 hover:bg-red-700 transition-all duration-200"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Home
                  </Link>
                  <Link
                    to="/pricing"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/pricing') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/contact"
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 ${
                      isActive('/contact') 
                        ? 'bg-white/20 text-white' 
                        : 'text-white/90 hover:bg-white/10 hover:text-white'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                  <div className="mt-4">
                    <Link
                      to="/login"
                      className="block w-full text-center px-4 py-2 border border-white/30 text-white rounded-md hover:bg-white/10 transition-all duration-200 mb-2"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block w-full text-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Start Free Trial
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
      
      {/* Spacer to push content below fixed navbar */}
      <div className="h-16"></div>
    </>
  );
};

export default Navbar;